import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, AlertCircle, CheckCircle, XCircle, User, Scissors, DollarSign, LayoutGrid, Rows } from 'lucide-react';

const AppointmentCalendar = ({ appointments = [], onDaySelect, onMonthChange, onViewModeChange }) => {
  const { t, i18n } = useTranslation('appointments');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  const [selectedDayAppointments, setSelectedDayAppointments] = useState([]);
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'weekly'

  // Filter out deleted appointments
  const filteredAppointments = appointments.filter(appointment => appointment.status !== 'deleted');

  // Status config with color mapping
  const statusConfig = {
    pending: { 
      label: t('status.pending'), 
      bgColor: 'bg-yellow-100', 
      textColor: 'text-yellow-800',
      icon: AlertCircle 
    },
    approved: { 
      label: t('status.approved'), 
      bgColor: 'bg-blue-100', 
      textColor: 'text-blue-800',
      icon: CheckCircle 
    },
    canceled: { 
      label: t('status.canceled'), 
      bgColor: 'bg-red-100', 
      textColor: 'text-red-800',
      icon: XCircle 
    },
    completed: { 
      label: t('status.completed'), 
      bgColor: 'bg-green-100', 
      textColor: 'text-green-800',
      icon: CheckCircle 
    },
    deleted: { 
      label: t('status.deleted'), 
      bgColor: 'bg-gray-100', 
      textColor: 'text-gray-800',
      icon: XCircle 
    }
  };

  const monthNames = t('calendar.months', { returnObjects: true });
  const dayNames = t('calendar.days', { returnObjects: true });

  // Function to get days in a week for weekly view
  const getDaysInWeek = (date) => {
    const days = [];
    const currentDay = new Date(date);
    
    // Get the Monday of the current week
    const dayOfWeek = currentDay.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday, go back 6 days, else go to Monday
    const monday = new Date(currentDay);
    monday.setDate(currentDay.getDate() + diff);
    
    // Generate 7 days starting from Monday
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      
      days.push({
        day: day.getDate(),
        month: day.getMonth(),
        year: day.getFullYear(),
        isCurrentMonth: true // All days in week view are considered "current"
      });
    }
    
    return days;
  };

  // Function to get days in month for calendar
  const getDaysInMonth = (year, month) => {
    // Get first day of month
    const firstDay = new Date(year, month, 1);
    let firstDayIndex = firstDay.getDay();
    // In JS, Sunday is 0, but we want Monday as 0
    firstDayIndex = firstDayIndex === 0 ? 6 : firstDayIndex - 1;
    
    // Get days in month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Get days in previous month
    const prevMonthDays = new Date(year, month, 0).getDate();
    
    const days = [];
    
    // Add previous month days
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        month: month - 1,
        year: month === 0 ? year - 1 : year,
        isCurrentMonth: false
      });
    }
    
    // Add current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true
      });
    }
    
    // Add next month days
    const remainingDays = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        month: month + 1,
        year: month === 11 ? year + 1 : year,
        isCurrentMonth: false
      });
    }
    
    return days;
  };

  // Update calendar days when month/week or view mode changes
  useEffect(() => {
    if (viewMode === 'weekly') {
      setCalendarDays(getDaysInWeek(currentDate));
    } else {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      setCalendarDays(getDaysInMonth(year, month));
    }
  }, [currentDate, viewMode]);

  // Handle month navigation
  const prevMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    setCurrentDate(newDate);
    setSelectedDate(null);
    
    // Notify parent component about month change
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  const nextMonth = () => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    setCurrentDate(newDate);
    setSelectedDate(null);
    
    // Notify parent component about month change
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  // Handle week navigation
  const prevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - 7);
    setCurrentDate(newDate);
    setSelectedDate(null);
    
    // Notify parent component about week change
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + 7);
    setCurrentDate(newDate);
    setSelectedDate(null);
    
    // Notify parent component about week change
    if (onMonthChange) {
      onMonthChange(newDate);
    }
  };

  // Format date to YYYY-MM-DD
  const formatDate = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Check if a day has appointments
  const hasAppointments = (day) => {
    if (!filteredAppointments || filteredAppointments.length === 0) return false;
    
    try {
      const dateStr = formatDate(day.year, day.month, day.day);
      return filteredAppointments.some(appointment => 
        appointment.date === dateStr && 
        appointment.status !== 'canceled'
      );
    } catch (error) {
      console.error("Error checking appointments for day:", error);
      return false;
    }
  };

  // Get appointment count by status for a specific day
  const getAppointmentCountByStatus = (day, status) => {
    if (!filteredAppointments || filteredAppointments.length === 0) return 0;
    
    try {
      const dateStr = formatDate(day.year, day.month, day.day);
      return filteredAppointments.filter(
        appointment => appointment.date === dateStr && appointment.status === status
      ).length;
    } catch (error) {
      console.error("Error counting appointments by status:", error);
      return 0;
    }
  };

  // Handle day selection
  const handleDayClick = (day) => {
    try {
      const dateStr = formatDate(day.year, day.month, day.day);
      setSelectedDate(dateStr);
      
      // Filter appointments for the selected day (excluding deleted ones)
      const dayAppointments = filteredAppointments.filter(
        appointment => appointment.date === dateStr
      );
      
      setSelectedDayAppointments(dayAppointments);
      
      if (onDaySelect) {
        onDaySelect(dateStr, dayAppointments);
      }
    } catch (error) {
      console.error("Error handling day click:", error);
    }
  };

  // Get today's date for highlighting
  const today = new Date();
  const todayStr = formatDate(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  // Navigate to current month/week
  const goToToday = () => {
    setCurrentDate(new Date());
    
    // Select today
    const today = new Date();
    const dateStr = formatDate(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    
    handleDayClick({
      day: today.getDate(),
      month: today.getMonth(),
      year: today.getFullYear(),
      isCurrentMonth: true
    });
  };

  // Toggle between monthly and weekly view
  const toggleViewMode = () => {
    const newViewMode = viewMode === 'monthly' ? 'weekly' : 'monthly';
    setViewMode(newViewMode);
    setSelectedDate(null);
    
    // Notify parent component about view mode change
    if (onViewModeChange) {
      onViewModeChange(newViewMode, currentDate);
    }
  };

  // Get week range for display
  const getWeekRange = () => {
    if (viewMode !== 'weekly' || calendarDays.length === 0) return '';
    
    const firstDay = calendarDays[0];
    const lastDay = calendarDays[calendarDays.length - 1];
    
    const firstDate = new Date(firstDay.year, firstDay.month, firstDay.day);
    const lastDate = new Date(lastDay.year, lastDay.month, lastDay.day);
    
    const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-GB';
    const formatOptions = { day: 'numeric', month: 'short' };
    const firstStr = firstDate.toLocaleDateString(dateLocale, formatOptions);
    const lastStr = lastDate.toLocaleDateString(dateLocale, formatOptions);
    
    return `${firstStr} - ${lastStr}, ${firstDay.year}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Calendar header */}
      <div className="p-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white">
        <div className="flex items-center justify-between mb-3">
          <button 
            onClick={viewMode === 'monthly' ? prevMonth : prevWeek}
            className="p-1 rounded-full hover:bg-blue-600 transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          
          <div className="flex flex-col items-center">
            <h3 className="text-lg font-semibold">
              {viewMode === 'monthly' 
                ? `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                : getWeekRange()
              }
            </h3>
            <button 
              onClick={goToToday}
              className="text-xs bg-white text-blue-600 px-2 py-0.5 rounded mt-1 hover:bg-blue-50"
            >
              {t('calendar.goToday')}
            </button>
          </div>
          
          <button 
            onClick={viewMode === 'monthly' ? nextMonth : nextWeek}
            className="p-1 rounded-full hover:bg-blue-600 transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        
        {/* View mode toggle */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={toggleViewMode}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              viewMode === 'monthly' 
                ? 'bg-white text-blue-600' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {t('calendar.monthly')}
          </button>
          <button
            onClick={toggleViewMode}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              viewMode === 'weekly' 
                ? 'bg-white text-blue-600' 
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {t('calendar.weekly')}
          </button>
        </div>
      </div>
      
      {/* Calendar grid */}
      <div className="p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day, index) => (
            <div key={index} className="text-center py-1 text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className={`grid grid-cols-7 gap-0 md:gap-1 ${viewMode === 'weekly' ? 'gap-2' : ''}`}>
          {calendarDays.map((day, index) => {
            const dateStr = formatDate(day.year, day.month, day.day);
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;
            const hasAppts = hasAppointments(day);
            
            // Count appointments by status
            const pendingCount = getAppointmentCountByStatus(day, 'pending');
            const approvedCount = getAppointmentCountByStatus(day, 'approved');
            const completedCount = getAppointmentCountByStatus(day, 'completed');
            
            // Get day name for weekly view
            const dayName = viewMode === 'weekly' ? dayNames[index] : null;
            
            return (
              <button
                key={index}
                onClick={() => handleDayClick(day)}
                className={`
                  ${viewMode === 'weekly' ? 'h-24 md:h-32 lg:h-40' : 'h-12 md:h-16 lg:h-20'}
                  flex flex-col items-center justify-center rounded-md text-sm md:text-base
                  ${!day.isCurrentMonth && viewMode === 'monthly' ? 'text-gray-400' : 'text-gray-800'} 
                  ${isToday ? 'bg-blue-100 font-bold ring-2 ring-blue-500' : ''}
                  ${isSelected ? 'bg-blue-500 text-white' : ''}
                  ${hasAppts && !isSelected ? 'font-bold' : ''}
                  hover:bg-blue-100 hover:text-blue-700 transition-colors
                  ${viewMode === 'weekly' ? 'border border-gray-200' : ''}
                `}
              >
                {viewMode === 'weekly' && (
                  <span className="text-xs text-gray-500 mb-1">{dayName}</span>
                )}
                <span className={viewMode === 'weekly' ? 'text-lg font-semibold' : ''}>{day.day}</span>
                {viewMode === 'weekly' && (
                  <span className="text-xs text-gray-500 mt-1">
                    {monthNames[day.month].substring(0, 3)}
                  </span>
                )}
                {hasAppts && (
                  <div className={`flex ${viewMode === 'weekly' ? 'flex-col gap-1' : 'space-x-1'} mt-1`}>
                    {pendingCount > 0 && (
                      <div className={`flex items-center gap-1 ${
                        viewMode === 'weekly' 
                          ? 'text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800' 
                          : ''
                      }`}>
                        <div className={`${viewMode === 'weekly' ? 'w-2 h-2' : 'w-1 h-1 md:w-1.5 md:h-1.5'} rounded-full shadow-sm ${isSelected ? 'bg-white' : 'bg-yellow-500'}`}></div>
                        {viewMode === 'weekly' && <span>{pendingCount}</span>}
                      </div>
                    )}
                    {approvedCount > 0 && (
                      <div className={`flex items-center gap-1 ${
                        viewMode === 'weekly' 
                          ? 'text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800' 
                          : ''
                      }`}>
                        <div className={`${viewMode === 'weekly' ? 'w-2 h-2' : 'w-1 h-1 md:w-1.5 md:h-1.5'} rounded-full shadow-sm ${isSelected ? 'bg-white' : 'bg-blue-600'}`}></div>
                        {viewMode === 'weekly' && <span>{approvedCount}</span>}
                      </div>
                    )}
                    {completedCount > 0 && (
                      <div className={`flex items-center gap-1 ${
                        viewMode === 'weekly' 
                          ? 'text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800' 
                          : ''
                      }`}>
                        <div className={`${viewMode === 'weekly' ? 'w-2 h-2' : 'w-1 h-1 md:w-1.5 md:h-1.5'} rounded-full shadow-sm ${isSelected ? 'bg-white' : 'bg-green-600'}`}></div>
                        {viewMode === 'weekly' && <span>{completedCount}</span>}
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AppointmentCalendar; 